import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSocket } from "./SocketContext";
import { useAuth } from "./AuthContext";
import {
  createThreadReply,
  getThreadReplies,
  markThreadAsRead as markThreadReadAPI,
  getThreadParticipants,
  updateThreadReply as updateThreadReplyAPI,
  deleteThreadReply as deleteThreadReplyAPI,
} from "../services/threadService";

const ThreadContext = createContext(null);

export function ThreadProvider({ children }) {
  const socket = useSocket();
  const { user } = useAuth();

  const [openThread, setOpenThread] = useState(null);
  const [threadReplies, setThreadReplies] = useState([]);
  const [threadMetadata, setThreadMetadata] = useState({
    replyCount: 0,
    participants: [],
    lastReplyAt: null,
  });
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadHasMore, setThreadHasMore] = useState(true);
  const [threadTypingUsers, setThreadTypingUsers] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // ── Use a ref for deduplication instead of state ──────────────────────────
  // State-based deduplication caused stale closures in socket handlers because
  // the Set captured at handler registration time was never updated.
  // A ref is always current without triggering re-renders or re-registrations.
  const processedThreadEventsRef = useRef(new Set());
  const loadingRef = useRef(false);

  // Keep a ref to openThread so socket handlers always read the latest value
  // without needing openThread in their dependency array (which would cause
  // the entire socket effect to re-register on every thread switch).
  const openThreadRef = useRef(openThread);
  useEffect(() => {
    openThreadRef.current = openThread;
  }, [openThread]);

  useEffect(() => {
    if (!socket) return;

    const handleNewReply = (reply) => {
      const replyId = reply._id?.toString();

      // Skip temp IDs (optimistic replies from our own send)
      if (replyId?.startsWith("temp_")) return;

      // Deduplicate via ref — always current, no stale closure
      const eventKey = `created:${replyId}`;
      if (processedThreadEventsRef.current.has(eventKey)) return;

      const currentUserId = user?._id?.toString();
      const replyUserId =
        reply.senderId?._id?.toString() || reply.senderId?.toString();

      // Read current thread from ref — always up-to-date
      const currentThread = openThreadRef.current;
      const parentId = currentThread?.parentMessage?._id;
      const replyParentId = reply.parentMessageId;

      const isSameThread =
        parentId &&
        replyParentId &&
        (parentId === replyParentId ||
          parentId?.toString() === replyParentId?.toString() ||
          parentId === replyParentId?.toString() ||
          parentId?.toString() === replyParentId);

      if (isSameThread) {
        processedThreadEventsRef.current.add(eventKey);

        // Skip own replies — already added optimistically in sendThreadReply
        if (currentUserId === replyUserId) return;

        setThreadReplies((prev) => {
          const exists = prev.some((r) => {
            const existingId = r._id?.toString();
            if (existingId?.startsWith("temp_")) return false;
            return existingId === replyId;
          });
          if (exists) return prev;
          return [...prev, reply];
        });

        setThreadMetadata((prev) => ({
          ...prev,
          replyCount: prev.replyCount + 1,
          lastReplyAt: reply.createdAt || new Date().toISOString(),
        }));
      }
    };

    const handleReplyUpdate = (reply) => {
      const replyId = reply._id?.toString();
      const eventKey = `updated:${replyId}`;
      if (processedThreadEventsRef.current.has(eventKey)) return;

      const currentThread = openThreadRef.current;
      const parentId = currentThread?.parentMessage?._id;
      const replyParentId = reply.parentMessageId;

      const isSameThread =
        parentId &&
        replyParentId &&
        (parentId === replyParentId ||
          parentId?.toString() === replyParentId?.toString() ||
          parentId === replyParentId?.toString() ||
          parentId?.toString() === replyParentId);

      if (isSameThread) {
        processedThreadEventsRef.current.add(eventKey);
        setThreadReplies((prev) =>
          prev.map((r) =>
            r._id === reply._id || r._id?.toString() === reply._id?.toString()
              ? reply
              : r,
          ),
        );
      }
    };

    const handleReplyDelete = ({ replyId, replyCount }) => {
      const eventKey = `deleted:${replyId}`;
      if (processedThreadEventsRef.current.has(eventKey)) return;

      processedThreadEventsRef.current.add(eventKey);
      setThreadReplies((prev) =>
        prev.filter(
          (r) =>
            r._id !== replyId && r._id?.toString() !== replyId?.toString(),
        ),
      );
      setThreadMetadata((prev) => ({
        ...prev,
        replyCount:
          replyCount !== undefined
            ? replyCount
            : Math.max(0, prev.replyCount - 1),
      }));
    };

    const handleThreadTyping = ({ userId, userName, typing }) => {
      setThreadTypingUsers((prev) => {
        const next = new Set(prev);
        if (typing) {
          next.add({ userId, userName });
        } else {
          for (const u of next) {
            if (u.userId === userId) next.delete(u);
          }
        }
        return next;
      });
    };

    socket.on("thread:reply:created", handleNewReply);
    socket.on("thread:reply:updated", handleReplyUpdate);
    socket.on("thread:reply:deleted", handleReplyDelete);
    socket.on("thread:typing", handleThreadTyping);

    return () => {
      socket.off("thread:reply:created", handleNewReply);
      socket.off("thread:reply:updated", handleReplyUpdate);
      socket.off("thread:reply:deleted", handleReplyDelete);
      socket.off("thread:typing", handleThreadTyping);
    };
    // Only re-register when socket instance changes — openThread is read via ref
  }, [socket, user?._id]);

  const openThreadPanel = useCallback(
    (parentMessage, chatId, chatType = "channel") => {
      const msgId = parentMessage._id || parentMessage.id;

      const currentId =
        openThreadRef.current?.parentMessage?._id ||
        openThreadRef.current?.parentMessage?.id;

      // Switching to a different thread — reset state
      if (currentId !== msgId) {
        setThreadReplies([]);
        setThreadMetadata({
          replyCount: 0,
          participants: [],
          lastReplyAt: null,
        });
        setCurrentPage(1);
        setThreadHasMore(true);
        processedThreadEventsRef.current = new Set();
        loadingRef.current = false;
      }

      setOpenThread({ parentMessage, chatId, chatType });
      socket?.emit("thread:join", { parentMessageId: msgId });
    },
    [socket],
  );

  const closeThreadPanel = useCallback(() => {
    if (openThreadRef.current) {
      socket?.emit("thread:leave", {
        parentMessageId: openThreadRef.current.parentMessage._id,
      });
    }
    setOpenThread(null);
    setThreadReplies([]);
    setThreadTypingUsers(new Set());
    processedThreadEventsRef.current = new Set();
    setThreadMetadata({
      replyCount: 0,
      participants: [],
      lastReplyAt: null,
    });
    setThreadHasMore(true);
    setCurrentPage(1);
    setThreadLoading(false);
    loadingRef.current = false;
  }, [socket]);

  const loadThreadReplies = useCallback(async (parentMessageId, page = 1) => {
    if (!parentMessageId) return;

    // For page 1: only block if already loading to prevent duplicate fetches.
    // Do NOT clear existing replies here — they were already cleared in
    // openThreadPanel when switching threads. Clearing here causes a flash
    // of empty content every time the modal opens.
    if (page === 1) {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setThreadLoading(true);
      // Only clear if we have no replies yet (first open, not a re-fetch)
      setThreadReplies((prev) => (prev.length === 0 ? [] : prev));
    }

    try {
      const result = await getThreadReplies(parentMessageId, page);

      const responseData = result?.data || result;
      const replies = responseData?.replies || [];
      const hasMore = responseData?.hasMore || false;
      const total = responseData?.total || 0;
      const fetchedPage = responseData?.page || 1;

      if (page === 1) {
        setThreadReplies(replies);
        setThreadMetadata({
          replyCount: total,
          participants: [],
          lastReplyAt:
            replies.length > 0 ? replies[replies.length - 1].createdAt : null,
        });
      } else {
        setThreadReplies((prev) => [...replies, ...prev]); // prepend older replies
        setThreadMetadata((prev) => ({
          ...prev,
          replyCount: total,
        }));
      }

      setThreadHasMore(hasMore);
      setCurrentPage(fetchedPage);
    } catch (error) {
      console.error("Failed to load thread replies:", error);
    } finally {
      if (page === 1) {
        loadingRef.current = false;
      }
      setThreadLoading(false);
    }
  }, []);

  const sendThreadReply = useCallback(
    async (content, type = "text", mentions = []) => {
      if (!openThreadRef.current || !content.trim()) return;

      const { parentMessage, chatId, chatType } = openThreadRef.current;
      const tempId = `temp_${Date.now()}`;

      // Build optimistic reply in the same shape ThreadMessage expects
      // so it renders correctly without waiting for the API response.
      const optimisticReply = {
        _id: tempId,
        parentMessageId: parentMessage._id,
        chatId,
        chatType,
        // senderId as object — matches populated server response shape
        senderId: {
          _id: user._id,
          name: user.name,
          profilePhoto: user.profilePhoto || null,
          username: user.username || null,
        },
        content,
        type,
        mentions,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isOptimistic: true,
      };

      setThreadReplies((prev) => [...prev, optimisticReply]);
      setThreadMetadata((prev) => ({
        ...prev,
        replyCount: prev.replyCount + 1,
        lastReplyAt: optimisticReply.createdAt,
      }));

      try {
        const result = await createThreadReply({
          parentMessageId: parentMessage._id,
          chatId,
          chatType,
          content,
          type,
          mentions,
        });

        const newReply = result?.data || result;

        if (newReply) {
          // Replace optimistic reply with confirmed server reply
          setThreadReplies((prev) =>
            prev.map((r) => (r._id === tempId ? newReply : r)),
          );
        }
      } catch (error) {
        console.error("Failed to send reply:", error);
        // Revert optimistic reply and count on failure
        setThreadReplies((prev) => prev.filter((r) => r._id !== tempId));
        setThreadMetadata((prev) => ({
          ...prev,
          replyCount: Math.max(0, prev.replyCount - 1),
        }));
      }
    },
    [user],
  );

  const updateThreadReply = useCallback(async (replyId, content) => {
    if (!replyId || !content.trim()) return;

    // Optimistic update
    setThreadReplies((prev) =>
      prev.map((r) =>
        r._id === replyId ? { ...r, content, isEditing: false } : r,
      ),
    );

    try {
      const result = await updateThreadReplyAPI(replyId, content);
      const updatedReply = result?.data || result;

      if (updatedReply) {
        setThreadReplies((prev) =>
          prev.map((r) =>
            r._id === replyId ? { ...updatedReply, isEditing: false } : r,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to update reply:", error);
      setThreadReplies((prev) =>
        prev.map((r) => (r._id === replyId ? { ...r, isEditing: false } : r)),
      );
    }
  }, []);

  const deleteThreadReply = useCallback(
    async (replyId) => {
      if (!replyId) return;

      // Optimistic delete
      setThreadReplies((prev) => prev.filter((r) => r._id !== replyId));
      setThreadMetadata((prev) => ({
        ...prev,
        replyCount: Math.max(0, prev.replyCount - 1),
      }));

      try {
        await deleteThreadReplyAPI(replyId);
      } catch (error) {
        console.error("Failed to delete reply:", error);
        // Re-fetch to restore correct state on failure
        if (openThreadRef.current?.parentMessage?._id) {
          loadThreadReplies(openThreadRef.current.parentMessage._id, 1);
        }
      }
    },
    [loadThreadReplies],
  );

  const markAsRead = useCallback(async (parentMessageId) => {
    if (!parentMessageId || !openThreadRef.current) return;
    try {
      await markThreadReadAPI(parentMessageId, openThreadRef.current.chatType);
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  }, []);

  const loadParticipants = useCallback(async (parentMessageId) => {
    if (!parentMessageId) return;
    try {
      const result = await getThreadParticipants(parentMessageId);
      setThreadMetadata((prev) => ({
        ...prev,
        participants: result.data,
      }));
    } catch (error) {
      console.error("Failed to load participants:", error);
    }
  }, []);

  const startThreadTyping = useCallback(() => {
    if (!openThreadRef.current) return;
    socket?.emit("thread:typing:start", {
      parentMessageId: openThreadRef.current.parentMessage._id,
      userId: user._id,
      userName: user.name,
    });
  }, [socket, user]);

  const stopThreadTyping = useCallback(() => {
    if (!openThreadRef.current) return;
    socket?.emit("thread:typing:stop", {
      parentMessageId: openThreadRef.current.parentMessage._id,
      userId: user._id,
      userName: user.name,
    });
  }, [socket, user]);

  const loadMoreReplies = useCallback(() => {
    if (openThreadRef.current && threadHasMore && !threadLoading) {
      loadThreadReplies(openThreadRef.current.parentMessage._id, currentPage + 1);
    }
  }, [threadHasMore, threadLoading, currentPage, loadThreadReplies]);

  const value = {
    openThread,
    threadReplies,
    threadMetadata,
    threadLoading,
    threadHasMore,
    threadTypingUsers,
    openThreadPanel,
    closeThreadPanel,
    loadThreadReplies,
    sendThreadReply,
    updateThreadReply,
    deleteThreadReply,
    markAsRead,
    startThreadTyping,
    stopThreadTyping,
    loadMoreReplies,
    loadParticipants,
  };

  return (
    <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
  );
}

export function useThread() {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error("useThread must be used within ThreadProvider");
  }
  return context;
}
